import React from "react";
import { badgePhoto, StatusEnum } from "../../hoc/with-status";

import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Card } from "@amzn/stencil-react-components/card";
import { Text, H3, H4 } from "@amzn/stencil-react-components/text";
import { renderBGColor } from "../task/ui-redesign";
import { HVH_COLORS } from "../../config/palette";
import { IconCheckCircleFill, IconSize } from "@amzn/stencil-react-components/icons";
import ProgressRing from "../screening-results/progress-ring.svg";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { useNavigatorWithURLReadyOnOpeningNewTab } from "../../hooks/use-navigator";
import { buildUrl, TAKE_BADGE_PHOTO_START_URL } from "../../config/urls";
import { useParams } from "react-router";
import useArb from "../../hooks/use-arb";

export interface BadgePhotoProps {
  status: StatusEnum;
  shouldOverrideStatus?: Record<string, StatusEnum>;
}

export const BadgePhotoTaskCardButton = ({ status, shouldOverrideStatus }: BadgePhotoProps) => {
  const bundle = useArb("HVHChecklistPantherResource");
  const translatedUpload = bundle.getMessage("Checklist-BPUpload-Button");
  const translatedReplace = bundle.getMessage("Checklist-BPUpload-Replace");

  const overrideStatus = shouldOverrideStatus ? shouldOverrideStatus[status] : status;
  const { applicationId, requisitionId } = useParams<{
    applicationId: string;
    requisitionId: string;
  }>();

  const buttonText = overrideStatus === StatusEnum.ACTION_REQUIRED ? translatedUpload : translatedReplace;

  return (
    <Button
      variant={ButtonVariant.Primary}
      onClick={useNavigatorWithURLReadyOnOpeningNewTab(
        buildUrl(TAKE_BADGE_PHOTO_START_URL, { requisitionId, applicationId })
      )}
      disabled={status === StatusEnum.COMPLETED}
    >
      {buttonText}
    </Button>
  );
};

export const BadgePhoto = ({ status, shouldOverrideStatus }: BadgePhotoProps) => {
  let badgePhotoStatus;
  let instructions;
  let displayLastUpload;

  const bundle = useArb("HVHChecklistPantherResource");
  const translatedStatus = bundle.getMessage("checklist-General-Status");
  const translatedApproved = bundle.getMessage("checklist-General-Approved");
  const translatedApprovalPending = bundle.getMessage("checklist-General-Approval-Pending");
  const translatedUploadCompleted = bundle.getMessage("Checklist-BPUpload-Completed");
  const translatedActionRequired = bundle.getMessage("checklist-General-TaskStatus-ActionRequired");
  const translatedDetails = bundle.getMessage("Checklist-CardControlled-DetailsTitle");
  const translatedInstruction = bundle.getMessage("Checklist-BPUploadRequirements-Good");
  const translatedRule1 = bundle.getMessage("Checklist-BPCameraRequirements-DoRule1");
  const translatedRule2 = bundle.getMessage("Checklist-BPCameraRequirements-DoRule2");
  const translatedRule3 = bundle.getMessage("Checklist-BPCameraRequirements-DoRule3");
  const translatedAvoid = bundle.getMessage("Checklist-BPCameraRequirements-Avoid");
  const translatedDoNotRule1 = bundle.getMessage("Checklist-BPCameraRequirements-DoNotRule1");
  const translatedDoNotRule2 = bundle.getMessage("Checklist-BPCameraRequirements-DoNotRule2");
  const translatedDoNotRule3 = bundle.getMessage("Checklist-BPCameraRequirements-DoNotRule3");

  switch (status) {
    case StatusEnum.NOT_STARTED || StatusEnum.API_ERROR:
      badgePhotoStatus = (
        <Col>
          <Col backgroundColor={renderBGColor(status, shouldOverrideStatus)} width={"6px"} />
          <Col gridGap={"1rem"} padding={"1rem"}>
            <H3 fontSize={"T400"}>{translatedStatus}</H3>
            <Text>{translatedActionRequired}</Text>
          </Col>
        </Col>
      );
      instructions = (
        <Col gridGap={"1rem"} padding={"1rem"}>
          <H3 id={"list-label-do"} fontSize={"T400"}>
            {translatedDetails}
          </H3>

          <H4 id={"list-label-do"}>{translatedInstruction}</H4>
          <ul aria-labelledby="list-label-do">
            <li>&#183; {translatedRule1}</li>
            <li>&#183; {translatedRule2}</li>
            <li>&#183; {translatedRule3}</li>
          </ul>

          <H4 id={"list-label-dont"}>{translatedAvoid}</H4>
          <ul aria-labelledby="list-label-dont">
            <li>&#183; {translatedDoNotRule1}</li>
            <li>&#183; {translatedDoNotRule2}</li>
            <li>&#183; {translatedDoNotRule3}</li>
          </ul>
        </Col>
      );
      break;
    case StatusEnum.COMPLETED:
      badgePhotoStatus = (
        <Col gridGap={"1rem"} padding={"1rem"} width={"100%"}>
          <H3 fontSize={"T400"}>{translatedStatus}</H3>
          <Row alignItems={"center"} gridGap={"0.5rem"}>
            <IconCheckCircleFill size={IconSize.Medium} color={HVH_COLORS.GREEN_70} />
            <Text>{translatedUploadCompleted}</Text>
          </Row>
          <Row alignItems={"center"} gridGap={"0.5rem"}>
            <IconCheckCircleFill size={IconSize.Medium} color={HVH_COLORS.GREEN_70} />
            <Text>{translatedApproved}</Text>
          </Row>
        </Col>
      );
      displayLastUpload = true;
      break;
    default:
      badgePhotoStatus = (
        <Col gridGap={"1rem"} padding={"1rem"} width={"100%"}>
          <H3 fontSize={"T400"}>{translatedStatus}</H3>
          <Row alignItems={"center"} gridGap={"0.5rem"}>
            <IconCheckCircleFill size={IconSize.Medium} color={HVH_COLORS.GREEN_70} />
            <Text>{translatedUploadCompleted}</Text>
          </Row>
          <Row alignItems={"center"} gridGap={"0.5rem"}>
            <img width={"24px"} height={"24px"} src={ProgressRing} alt={""} />
            <Text>{translatedApprovalPending}</Text>
          </Row>
        </Col>
      );
      displayLastUpload = true;
      break;
  }

  return (
    <Col padding={"1rem"} gridGap={"1rem"}>
      <Card padding={"0rem"}>
        <Col backgroundColor={renderBGColor(status, shouldOverrideStatus)} width={"6px"} />
        {badgePhotoStatus}
      </Card>

      {instructions && (
        <Card padding={"0rem"}>
          <Col>
            <Col backgroundColor={renderBGColor(status, shouldOverrideStatus)} width={"6px"} />
            {instructions}
          </Col>
        </Card>
      )}
      {displayLastUpload && badgePhoto && (
        <Col alignItems={"center"} padding={"0rem 1rem 1rem 1rem"}>
          <img width={"70%"} src={badgePhoto} alt={"badge-photo"} />
        </Col>
      )}
    </Col>
  );
};
